import { Eye, Users, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import type { DailyTaskComment } from '@/features/daily-tasks/services/daily-tasks.service';
import { getMentionDisplayName } from '@/features/daily-tasks/utils/mention-utils';

function getDisplayLabel(user?: {
  fullName?: string | null;
  email: string;
} | null): string {
  if (!user) return 'Usuário';
  return user.fullName?.trim() || user.email;
}

export function CommentViewsIndicator({ comment }: { comment: DailyTaskComment }) {
  const views = comment.views ?? [];
  const mentions = comment.mentions ?? [];
  const viewedUserIds = new Set(views.map((view) => view.userId));
  const notViewedMentions = mentions.filter(
    (mention) => !viewedUserIds.has(mention.userId),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 text-xs text-muted-foreground"
          aria-label={`Visualizações: ${views.length}`}
        >
          <Eye className="h-3.5 w-3.5" />
          <span>{views.length}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Visualizações</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {views.length === 0 ? (
          <DropdownMenuItem disabled>
            <span className="text-muted-foreground">Nenhuma visualização ainda</span>
          </DropdownMenuItem>
        ) : (
          views.map((view) => (
            <DropdownMenuItem key={view.id} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 truncate">
                <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                {getDisplayLabel(view.user)}
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {formatDistanceToNow(new Date(view.viewedAt), {
                  addSuffix: true,
                  locale: ptBR,
                })}
              </span>
            </DropdownMenuItem>
          ))
        )}
        {notViewedMentions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-muted-foreground">
              Ainda não visualizaram
            </DropdownMenuLabel>
            {notViewedMentions.map((mention) => (
              <DropdownMenuItem key={`pending-${mention.id}`} disabled>
                <span className="truncate text-muted-foreground">
                  {getDisplayLabel(mention.user)}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CommentMentionsIndicator({ comment }: { comment: DailyTaskComment }) {
  const mentions = comment.mentions ?? [];
  if (mentions.length === 0) return null;

  const mentionNames = mentions
    .map((mention) => {
      const label = mention.user
        ? getMentionDisplayName(mention.user)
        : getDisplayLabel(mention.user);
      return label;
    })
    .filter(Boolean);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
        >
          <Users className="h-3.5 w-3.5" />
          {mentionNames.length <= 2
            ? `Mencionados: ${mentionNames.join(', ')}`
            : `Mencionados: ${mentionNames.slice(0, 2).join(', ')} +${mentionNames.length - 2}`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Mencionados</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {mentions.map((mention) => (
          <DropdownMenuItem key={mention.id}>
            <span className="truncate">
              {mention.user ? getDisplayLabel(mention.user) : mention.userId}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
